/**
 * trailSwatch — a trail card's thumbnail, drawn by the *real* trail style.
 *
 * Styles in rendering/pixi/trailStyles.js draw into a PIXI.Graphics
 * (moveTo/lineTo/stroke, circle/fill). graphicsAdapter() gives a 2D canvas
 * context that same surface, so a 96×40 swatch shows exactly the look the
 * renderer produces, without a WebGL context per card. Pure: no PIXI import.
 */
import { getTrailStyle, toColor } from '../rendering/pixi/trailStyles.js';

const hex = (c) => '#' + (toColor(c) >>> 0).toString(16).padStart(6, '0');

export function graphicsAdapter(ctx) {
  let path = [];
  return {
    moveTo(x, y) { path.push(['m', x, y]); },
    lineTo(x, y) { path.push(['l', x, y]); },
    circle(x, y, r) { path.push(['c', x, y, r]); },
    stroke({ width = 1, color = 0xffffff, alpha = 1, cap = 'round' } = {}) {
      ctx.beginPath();
      for (const p of path) { if (p[0] === 'm') ctx.moveTo(p[1], p[2]); else if (p[0] === 'l') ctx.lineTo(p[1], p[2]); }
      ctx.lineWidth = width; ctx.strokeStyle = hex(color); ctx.globalAlpha = alpha; ctx.lineCap = cap;
      ctx.stroke();
      path = [];
    },
    fill({ color = 0xffffff, alpha = 1 } = {}) {
      ctx.beginPath();
      for (const p of path) if (p[0] === 'c') { ctx.moveTo(p[1] + p[3], p[2]); ctx.arc(p[1], p[2], p[3], 0, Math.PI * 2); }
      ctx.fillStyle = hex(color); ctx.globalAlpha = alpha;
      ctx.fill();
      path = [];
    },
    clear() { path = []; }
  };
}

/**
 * Draw one trail definition (a config/cosmetics.json `trails` entry) as a
 * horizontal swatch: newest sample at the right, fading toward the left.
 */
export function renderTrailSwatch(ctx, def, { width, height, now = 1000 }) {
  ctx.clearRect(0, 0, width, height);
  const style = getTrailStyle(def.style) || getTrailStyle('ribbon');
  const lifeMs = def.lifeMs ?? 500;
  const n = 12;
  const samples = [];
  for (let i = 0; i < n; i++) {
    const f = i / (n - 1);                                   // 0 = oldest (left), 1 = newest (right)
    samples.push({ x: 8 + f * (width - 16), y: height / 2 + Math.sin(f * Math.PI) * height * 0.15, t: now - (1 - f) * lifeMs * 0.9 });
  }
  const g = graphicsAdapter(ctx);
  style.draw(g, samples, {
    now, lifeMs, color: toColor(def.color), secondaryColor: def.secondaryColor != null ? toColor(def.secondaryColor) : null,
    width: Math.min(def.width ?? 14, height * 0.45), alpha: def.alpha ?? 0.55, isLocal: true
  });
}
